import React, { useState } from 'react'
import styled from 'styled-components'
import { TextArea } from 'sutra/components/Input/TextArea'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import { darkBlueFontColor } from 'assets/styles/variables'

interface IssueFeedbackModalProps {
  isVisible: boolean
  isPositive: boolean | null
  onSubmit: (isPositive: boolean, additionalDetails: string) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}

const IssueFeedbackModal: React.FC<IssueFeedbackModalProps> = ({
  isVisible,
  isPositive,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [details, setDetails] = useState('')

  const handleSubmit = async (): Promise<void> => {
    if (isPositive === null) return
    await onSubmit(isPositive, details)
    setDetails('')
  }

  const handleCancel = (): void => {
    setDetails('')
    onCancel()
  }

  const title = 'Help us improve this insight'

  const prompt = isPositive
    ? '👍 What about this insight was helpful?'
    : "👎 What's missing or off about this insight?"

  const placeholder = isPositive
    ? "This matches what I'm seeing in other data"
    : 'Too vague / not actionable / wrong root cause'

  return (
    <PopoverModal
      title={title}
      isOpen={isVisible}
      onOk={handleSubmit}
      onClose={handleCancel}
      confirmLoading={isLoading}
      okText="Submit Feedback"
      okButtonProps={{
        disabled: isLoading,
      }}
      cancelButtonProps={{
        disabled: isLoading,
      }}
      width={500}
      content={
        <ModalContent>
          <PromptText>{prompt}</PromptText>
          <StyledTextArea
            rows={4}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={placeholder}
            maxLength={5000}
            disabled={isLoading}
          />
          <CharacterCount>{details.length} / 5000 characters</CharacterCount>
        </ModalContent>
      }
    />
  )
}

export default IssueFeedbackModal

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;
`

const PromptText = styled.p`
  font-size: 14px;
  color: ${darkBlueFontColor};
  margin: 0;
  font-weight: 500;
`

const StyledTextArea = styled(TextArea)`
  font-size: 14px;

  &::placeholder {
    color: #999;
    font-style: italic;
  }
`

const CharacterCount = styled.span`
  font-size: 12px;
  color: ${darkBlueFontColor};
  opacity: 0.6;
  text-align: right;
`
